import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@/v2/components/common/Button'
import PatientAvatar from '@/v2/components/common/PatientAvatar'
import Message from '@/v2/containers/messages/MessagesHistory/Message'
import { sortByCreation } from '@/v2/containers/messages/MessagesHistory/utils'
import { useAppointmentThread } from '@/v2/hooks/appointments/useUnconfirmedAppointments'
import { safeFormat } from '@/v2/utils/convert'
import { formatPhone, healthHistoryOf, isHealthHistoryDone } from '../utils'
import { HealthHistory } from '../styles'

const Panel = styled.div`
  width: min(440px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 72px;
  padding: 0 12px 0 20px;
  border-bottom: 1px solid #eeeded;
  flex: none;
`

// Its own component: a bare div selector in Head would also match the
// avatar's root div and stretch the circle into an oval.
const Who = styled.div`
  flex: 1;
  min-width: 0;

  b {
    display: block;
    font-weight: 500;
  }

  span {
    color: #7d7d7d;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }
`

const Context = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 20px;
  background: #f9f9f9;
  font-size: 13px;
  flex: none;
`

const Thread = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`

const Note = styled.div`
  padding: 12px 14px;
  border-radius: 4px;
  background: #f9f9f9;
  color: #7d7d7d;
  font-size: 13px;
`

const Composer = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid #eeeded;
  flex: none;
`

const Quick = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #7d7d7d;

  button {
    padding: 4px 10px 3px;
    border: 1px solid #eeeded;
    border-radius: 16px;
    background: #fff;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      background: #eeeded;
    }
  }
`

const SendRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;

  textarea {
    flex: 1;
    min-height: 64px;
    padding: 8px 10px;
    border: 1px solid #eeeded;
    border-radius: 4px;
    background: #fff;
    color: #111;
    font-family: inherit;
    font-size: 14px;
    resize: none;
  }
`

const quickTexts = appointment => {
  const { patient, appointmentType, startsAt } = appointment
  const when = `${safeFormat(startsAt, 'EEE, MMM d')} at ${safeFormat(
    startsAt,
    'h:mm a'
  )}`

  return [
    {
      label: 'Ask to confirm',
      text: `Hi ${patient.firstName}, can you confirm your ${appointmentType?.title} on ${when}? Just reply to this text.`,
    },
    !isHealthHistoryDone(appointment) &&
      patient.intakeForm?.url && {
        label: 'Health history link',
        text: `Hi ${patient.firstName}, please fill out your health history before your appointment: ${patient.intakeForm.url}`,
      },
  ].filter(Boolean)
}

const MessagesDrawer = ({ appointment, onClose }) => {
  const conversationId = appointment?.conversationId
  const { messages, isLoading, sendMessage } =
    useAppointmentThread(conversationId)
  const [text, setText] = useState('')
  const box = useRef(null)
  const thread = useRef(null)

  useEffect(() => setText(''), [conversationId])

  useEffect(() => {
    if (thread.current) thread.current.scrollTop = thread.current.scrollHeight
  }, [messages])

  const send = () => {
    if (!text.trim()) return
    sendMessage(text.trim())
    setText('')
  }

  const healthHistory = appointment && healthHistoryOf(appointment)

  return (
    <Drawer anchor="right" open={Boolean(appointment)} onClose={onClose}>
      {appointment && (
        <Panel aria-label="Messages">
          <Head>
            <PatientAvatar
              patient={appointment.patient}
              size="40px"
              sx={{ flexShrink: 0 }}
            />
            <Who>
              <b>{appointment.patient.fullName}</b>
              <span>{formatPhone(appointment.patient.phoneNumber)}</span>
            </Who>
            <Button
              variant="text"
              color="link"
              href={`/v2/messages/${conversationId}`}
            >
              Open in Messages
            </Button>
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Head>

          <Context>
            <span>
              {appointment.appointmentType?.title} ·{' '}
              {safeFormat(appointment.startsAt, 'EEE, MMM d, h:mm a')}
            </span>
            <HealthHistory $tone={healthHistory.tone}>
              Health history: {healthHistory.label}
            </HealthHistory>
          </Context>

          <Thread ref={thread}>
            {isLoading ? (
              <CircularProgress sx={{ display: 'block', mx: 'auto' }} />
            ) : messages.length ? (
              sortByCreation(messages).map(message => (
                <Message key={message.id} message={message} />
              ))
            ) : (
              <Note>No texts with {appointment.patient.firstName} yet.</Note>
            )}
          </Thread>

          <Composer>
            <Quick>
              Quick text:
              {quickTexts(appointment).map(quick => (
                <button
                  key={quick.label}
                  type="button"
                  onClick={() => {
                    setText(quick.text)
                    box.current?.focus()
                  }}
                >
                  {quick.label}
                </button>
              ))}
            </Quick>
            <SendRow>
              <textarea
                ref={box}
                aria-label="Message"
                placeholder="Type a text message..."
                value={text}
                onChange={event => setText(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    send()
                  }
                }}
              />
              <Button
                variant="contained"
                color="secondary"
                disabled={!text.trim()}
                onClick={send}
              >
                Send
              </Button>
            </SendRow>
          </Composer>
        </Panel>
      )}
    </Drawer>
  )
}

export default MessagesDrawer
